import { ImageConfig, ImageLoader } from "@angular/common";
import { inject } from "@angular/core";

import {
    buildPath,
    buildScene7FitParameter,
    computeScene7Dimensions,
    getSpecialScene7BackgroundURLParameter,
    isSpecialScene7BackgroundURLParameter,
} from "./scene7-image-loader.helper";
import { Scene7ImageLoaderConfig } from "./scene7-image-loader.model";
import { SCENE7_IMAGE_SETTINGS } from "./scene7-image-settings";

/**
 * See reference docs here: https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/c-command-reference.html?lang=en
 *
 * @see https://experienceleague.adobe.com/docs/dynamic-media-developer-resources/image-serving-api/image-serving-api/http-protocol-reference/command-reference/c-command-reference.html?lang=en
 */
export const scene7ImageLoaderFactory = (): ImageLoader => {
    const settings = inject(SCENE7_IMAGE_SETTINGS);

    return ({
        src,
        width,
        loaderParams: loaderParameters = {},
    }: Scene7ImageLoaderConfig): string => {
        const url = new URL(buildPath(src, settings));
        const specialSearchParameters: string[] = [];
        // TODO ng21: use new "height" param from "Scene7ImageLoaderConfig" (Angular ImageLoaderConfig)
        const { width: computedWidth, height: computedHeight } =
            computeScene7Dimensions(width, loaderParameters, settings);

        if (!loaderParameters.keepSourceParams) {
            // Cleanup parameters managed by this loader
            // BUT do not clean all other query params (that are typically
            // present for composite images)
            url.searchParams.delete("wid");
            url.searchParams.delete("hei");
            url.searchParams.delete("size");
            url.searchParams.delete("fit");
            url.searchParams.delete("qlt");
            url.searchParams.delete("fmt");
            url.searchParams.delete("op_sharpen");
            url.searchParams.delete("resMode");
            url.searchParams.delete("bgc");
            url.searchParams.delete("bgColor");
        }

        if (computedWidth) {
            url.searchParams.append("wid", `${computedWidth}`);
        }

        if (computedHeight) {
            url.searchParams.append("hei", `${computedHeight}`);
        }

        // fit parameter requires at least width or height
        if (loaderParameters.fit && (computedWidth || computedHeight)) {
            url.searchParams.append(
                "fit",
                buildScene7FitParameter(
                    loaderParameters.fit,
                    loaderParameters.upscale,
                ),
            );
        }

        if (loaderParameters.quality) {
            url.searchParams.append("qlt", `${loaderParameters.quality}`);
        }

        if (loaderParameters?.format) {
            url.searchParams.append("fmt", loaderParameters.format);
        }

        if (loaderParameters?.extend) {
            url.searchParams.delete("extend");
            specialSearchParameters.push(`extend=${loaderParameters.extend}`);
        }

        if (loaderParameters?.sharpen) {
            url.searchParams.append("op_sharpen", "1");
        }

        if (loaderParameters?.resamplingMode) {
            url.searchParams.append("resMode", loaderParameters.resamplingMode);
        }

        if (
            isSpecialScene7BackgroundURLParameter(loaderParameters.background)
        ) {
            specialSearchParameters.push(
                getSpecialScene7BackgroundURLParameter(
                    loaderParameters.background,
                ),
            );
        } else if (loaderParameters.background) {
            url.searchParams.append("bgc", loaderParameters.background);
            url.searchParams.append("bgColor", loaderParameters.background);
        }

        let stringifiedUrl = url.toString();

        // Manually append special search params without using "url.searchParams",
        // because it has few limitations:
        // - it always adds "=" following the key (which is correct according to specs)
        // - it encodes special chars (such as $)
        if (specialSearchParameters.length > 0) {
            const symbol = url.search ? "&" : "?";

            stringifiedUrl += symbol + specialSearchParameters.join("&");
        }

        // Scene7 does not support encoded version of some characters ("$" and "," notably)
        return stringifiedUrl.replace(/\?[^#]*/, (q) => decodeURIComponent(q));
    };
};

// Copied from Angular's private IMAGE_CONFIG_DEFAULTS
const defaultBreakpoints = [
    16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048,
    3840,
];

export const scene7ImageConfigFactory = (): ImageConfig => {
    const { maxWidth } = inject(SCENE7_IMAGE_SETTINGS);

    return {
        breakpoints: defaultBreakpoints.filter(
            (breakpoint) => breakpoint <= maxWidth,
        ),
    };
};
